import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gauge, Clock, Sun, CloudRain, CloudSnow, AlertTriangle, HelpCircle } from 'lucide-react';
import styles from './BrakingDistanceSimulator.module.css';

interface BrakingDistanceSimulatorProps {
    label?: string;
    description?: string;
}

type RoadCondition = 'dry' | 'wet' | 'ice';

export const BrakingDistanceSimulator: React.FC<BrakingDistanceSimulatorProps> = ({
    label = 'Stopping Distance Simulator',
    description = 'Calculate Total Stopping Distance based on Speed, Road Condition, and Reaction Time.'
}) => {
    // Inputs
    const [speed, setSpeed] = useState<number>(50); // mph
    const [reactionTime, setReactionTime] = useState<number>(1.5); // seconds
    const [roadCondition, setRoadCondition] = useState<RoadCondition>('dry');
    const [showComparison, setShowComparison] = useState<boolean>(true);

    // Physics constants
    // Coefficient of friction for tires under different road conditions
    const frictionCoefficients: Record<RoadCondition, number> = {
        dry: 0.7,
        wet: 0.4,
        ice: 0.15
    };

    const mu = frictionCoefficients[roadCondition];

    // Math Calculations
    // 1 mph = 1.467 feet per second
    const FPS_CONVERSION = 1.467;

    const calculateMetrics = (v: number) => {
        const reactionDist = v * FPS_CONVERSION * reactionTime;
        const brakingDist = (v * v) / (30 * mu);
        const totalDist = reactionDist + brakingDist;
        return {
            reaction: Math.round(reactionDist * 10) / 10,
            braking: Math.round(brakingDist * 10) / 10,
            total: Math.round(totalDist * 10) / 10
        };
    };

    const carAMetrics = useMemo(() => calculateMetrics(speed), [speed, reactionTime, mu]);
    const carBMetrics = useMemo(() => calculateMetrics(speed + 10), [speed, reactionTime, mu]);

    // Calculate Car B's impact speed when it reaches Car A's complete stop distance
    // This perfectly demonstrates the danger of +10 mph
    const comparisonAnalysis = useMemo(() => {
        const vA = speed;
        const vB = speed + 10;
        const dStopA = carAMetrics.total;

        // Distance B travels in reaction phase
        const dRxnB = vB * FPS_CONVERSION * reactionTime;

        if (dStopA <= dRxnB) {
            // Car B is still in reaction phase when A has stopped!
            // That means it hasn't even hit the brakes yet, so it is still traveling at full speed!
            return {
                impactSpeed: vB,
                isFatal: true,
                phase: 'reaction'
            };
        }

        // Distance B has spent braking when it reaches A's stopping distance
        const dBrakeB = dStopA - dRxnB;

        // Calculate current speed of B at that point using work-energy theorem
        // v_f^2 = v_i^2 - 2 * a * d  -> v_f = sqrt(v_i^2 - 30 * mu * d_braking)
        const speedSquared = (vB * vB) - (30 * mu * dBrakeB);
        const impactSpeed = speedSquared > 0 ? Math.sqrt(speedSquared) : 0;

        return {
            impactSpeed: Math.round(impactSpeed * 10) / 10,
            isFatal: impactSpeed > 20, // 20+ mph impact is highly dangerous
            phase: 'braking'
        };
    }, [speed, reactionTime, mu, carAMetrics]);

    // Scale calculations for visualization tracks
    // The maximum possible total distance is mapped to 85% of track width
    const maxTrackDistance = showComparison ? Math.max(carAMetrics.total, carBMetrics.total) : carAMetrics.total;
    const trackPadding = maxTrackDistance * 1.15; // 15% buffer at the end of the road

    const getPercentage = (value: number) => {
        return (value / trackPadding) * 100;
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.icon}>
                    <Gauge size={24} />
                </div>
                <h3 className={styles.title}>{label}</h3>
            </div>
            <p className={styles.description}>{description}</p>

            <div className={styles.mainGrid}>
                {/* Controls Column */}
                <div className={styles.controlsColumn}>
                    {/* Speed Control */}
                    <div className={styles.controlGroup}>
                        <div className={styles.controlHeader}>
                            <label className={styles.label} htmlFor="speed-slider">Speed</label>
                            <span className={styles.valueBadge}>{speed} MPH</span>
                        </div>
                        <input
                            id="speed-slider"
                            type="range"
                            min="20"
                            max="70"
                            step="5"
                            value={speed}
                            onChange={(e) => setSpeed(Number(e.target.value))}
                            className={styles.slider}
                        />
                    </div>

                    {/* Reaction Time Control */}
                    <div className={styles.controlGroup}>
                        <div className={styles.controlHeader}>
                            <label className={styles.label} htmlFor="reaction-slider">Reaction Time</label>
                            <span className={styles.valueBadge}>{reactionTime}s</span>
                        </div>
                        <input
                            id="reaction-slider"
                            type="range"
                            min="0.5"
                            max="2.5"
                            step="0.1"
                            value={reactionTime}
                            onChange={(e) => setReactionTime(Number(e.target.value))}
                            className={styles.slider}
                        />
                    </div>

                    {/* Road Conditions Tabs */}
                    <div className={styles.controlGroup}>
                        <label className={styles.label}>Road Condition</label>
                        <div className={styles.tabGroup}>
                            <button
                                type="button"
                                className={`${styles.tabButton} ${roadCondition === 'dry' ? styles.tabButtonActive : ''}`}
                                onClick={() => setRoadCondition('dry')}
                            >
                                <Sun size={16} />
                                <span>Dry</span>
                            </button>
                            <button
                                type="button"
                                className={`${styles.tabButton} ${roadCondition === 'wet' ? styles.tabButtonActive : ''}`}
                                onClick={() => setRoadCondition('wet')}
                            >
                                <CloudRain size={16} />
                                <span>Wet</span>
                            </button>
                            <button
                                type="button"
                                className={`${styles.tabButton} ${roadCondition === 'ice' ? styles.tabButtonActive : ''}`}
                                onClick={() => setRoadCondition('ice')}
                            >
                                <CloudSnow size={16} />
                                <span>Ice/Snow</span>
                            </button>
                        </div>
                    </div>

                    {/* Comparison Toggle */}
                    <div className={styles.compareToggleWrapper}>
                        <span className={styles.toggleLabel}>Compare with +10 MPH Vehicle</span>
                        <label className={styles.toggleSwitch} htmlFor="compare-toggle">
                            <input
                                id="compare-toggle"
                                type="checkbox"
                                checked={showComparison}
                                onChange={(e) => setShowComparison(e.target.checked)}
                            />
                            <span className={styles.toggleSlider}></span>
                        </label>
                    </div>
                </div>

                {/* Metrics Cards Column */}
                <div className={styles.metricsColumn}>
                    <div className={styles.mainMetricCard}>
                        <span className={styles.metricTitle}>Total Stopping Distance</span>
                        <span className={styles.metricValue}>{carAMetrics.total} ft</span>
                        <span className={styles.metricSub}>Equivalent to ~{Math.round(carAMetrics.total / 15)} car lengths</span>
                    </div>

                    <div className={styles.subMetricsGrid}>
                        <div className={styles.subMetricCard}>
                            <span className={styles.subMetricLabel}>Reaction Dist</span>
                            <span className={`${styles.subMetricValue} ${styles.colorAmber}`}>{carAMetrics.reaction} ft</span>
                            <span className={styles.metricSub}>{Math.round(speed * FPS_CONVERSION)} ft/s velocity</span>
                        </div>
                        <div className={styles.subMetricCard}>
                            <span className={styles.subMetricLabel}>Braking Dist</span>
                            <span className={`${styles.subMetricValue} ${styles.colorRed}`}>{carAMetrics.braking} ft</span>
                            <span className={styles.metricSub}>Friction coeff: {mu}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Road Visualization Section */}
            <div className={styles.visualization}>
                <div className={styles.roadContainer}>
                    {/* Car A Lane */}
                    <div className={styles.roadLane}>
                        <div className={styles.roadDividers} />
                        
                        {/* Reaction Zone Overlay */}
                        <div 
                            className={`${styles.overlayZone} ${styles.reactionZone}`} 
                            style={{ width: `${getPercentage(carAMetrics.reaction)}%`, left: 0 }}
                        />
                        
                        {/* Braking Zone Overlay */}
                        <div 
                            className={`${styles.overlayZone} ${styles.brakingZone}`} 
                            style={{ 
                                width: `${getPercentage(carAMetrics.braking)}%`, 
                                left: `${getPercentage(carAMetrics.reaction)}%` 
                            }}
                        />

                        {/* Animated Car A */}
                        <motion.div
                            className={styles.animatedCar}
                            animate={{ left: `${getPercentage(carAMetrics.total)}%` }}
                            transition={{ type: 'spring', stiffness: 45, damping: 15 }}
                            style={{ x: '-100%', marginLeft: '-4px' }}
                        >
                            <svg width="32" height="16" viewBox="0 0 24 12" fill="currentColor">
                                <path d="M19 1H5C3.34 1 2 2.34 2 4V8C2 9.66 3.34 11 5 11H19C20.66 11 22 9.66 22 8V4C22 2.34 20.66 1 19 1ZM5.5 10C4.67 10 4 9.33 4 8.5C4 7.67 4.67 7 5.5 7C6.33 7 7 7.67 7 8.5C7 9.33 6.33 10 5.5 10ZM18.5 10C17.67 10 17 9.33 17 8.5C17 7.67 17.67 7 18.5 7C19.33 7 20 7.67 20 8.5C20 9.33 19.33 10 18.5 10Z"/>
                            </svg>
                            <span className={styles.carLabel}>{speed} mph</span>
                        </motion.div>

                        <div className={styles.hazardMarker}>
                            <AlertTriangle size={20} />
                        </div>
                    </div>

                    {/* Car B (+10 MPH) Lane */}
                    <AnimatePresence>
                        {showComparison && (
                            <motion.div 
                                className={styles.roadLane}
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 48, opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                <div className={styles.roadDividers} />
                                
                                {/* Reaction Zone B */}
                                <div 
                                    className={`${styles.overlayZone} ${styles.reactionZone}`} 
                                    style={{ width: `${getPercentage(carBMetrics.reaction)}%`, left: 0 }}
                                />
                                
                                {/* Braking Zone B */}
                                <div 
                                    className={`${styles.overlayZone} ${styles.brakingZone}`} 
                                    style={{ 
                                        width: `${getPercentage(carBMetrics.braking)}%`, 
                                        left: `${getPercentage(carBMetrics.reaction)}%` 
                                    }}
                                />

                                {/* Animated Car B */}
                                <motion.div
                                    className={`${styles.animatedCar} ${styles.animatedCarAlt}`}
                                    animate={{ left: `${getPercentage(carBMetrics.total)}%` }}
                                    transition={{ type: 'spring', stiffness: 45, damping: 15 }}
                                    style={{ x: '-100%', marginLeft: '-4px' }}
                                >
                                    <svg width="32" height="16" viewBox="0 0 24 12" fill="currentColor">
                                        <path d="M19 1H5C3.34 1 2 2.34 2 4V8C2 9.66 3.34 11 5 11H19C20.66 11 22 9.66 22 8V4C22 2.34 20.66 1 19 1ZM5.5 10C4.67 10 4 9.33 4 8.5C4 7.67 4.67 7 5.5 7C6.33 7 7 7.67 7 8.5C7 9.33 6.33 10 5.5 10ZM18.5 10C17.67 10 17 9.33 17 8.5C17 7.67 17.67 7 18.5 7C19.33 7 20 7.67 20 8.5C20 9.33 19.33 10 18.5 10Z"/>
                                    </svg>
                                    <span className={styles.carLabel}>{speed + 10} mph</span>
                                </motion.div>

                                <div className={styles.hazardMarker}>
                                    <AlertTriangle size={20} />
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Physics Comparison Callout */}
                {showComparison && (
                    <div className={styles.comparisonCallout}>
                        <AlertTriangle className={styles.comparisonIcon} size={18} />
                        <p className={styles.comparisonText}>
                            <strong>The Velocity Hazard:</strong> Going just 10 mph faster ({speed + 10} mph vs {speed} mph) 
                            increases your kinetic energy by <strong>{Math.round(((speed + 10) ** 2 / (speed ** 2) - 1) * 100)}%</strong>. 
                            At the point where the {speed} mph car stops completely, the {speed + 10} mph car is 
                            still traveling at <strong>{comparisonAnalysis.impactSpeed} MPH</strong>. This speed is easily 
                            enough to cause a {comparisonAnalysis.isFatal ? 'fatal' : 'severe'} crash!
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BrakingDistanceSimulator;
